import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

/**
 * Kid session context — lives side-by-side with the parent AuthContext.
 * Reads from the separate `mtt_kid_session` httpOnly cookie via the
 * backend's /api/auth/kid-me probe on mount, so a browser tab already
 * logged into a kid session hydrates automatically.
 */
const KidAuthContext = createContext({
  kid: null,      // null = loading, false = signed out, {…} = signed in
  refresh: async () => {},
  logout: async () => {},
});

export function KidAuthProvider({ children }) {
  const [kid, setKid] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/kid-me");
      setKid(data);
    } catch (_e) {
      setKid(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/kid-logout"); } catch (_e) { /* still clear */ }
    setKid(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <KidAuthContext.Provider value={{ kid, refresh, logout }}>
      {children}
    </KidAuthContext.Provider>
  );
}

export function useKidAuth() {
  return useContext(KidAuthContext);
}
